import { useEffect, useRef, useState } from 'react';
import { campaign } from './campaign';
import ModalCta from './ModalCta';
import '../../styles/Campaign.css';
import '../../styles/CampaignMobile.css';

/**
 * Bottom-sheet de campaña (HU-016, lámina 04) — se abre al tocar
 * `CampaignStory`. Accesible como pide el diseño aprobado: `role="dialog"`,
 * foco atrapado adentro mientras está abierto, Esc/backdrop/swipe-down
 * cierran, y el foco vuelve a lo que estaba enfocado antes al cerrarse.
 */
const CampaignSheet = ({ configuracion, onCerrar }) => {
  const sheetRef = useRef(null);
  const [arrastreY, setArrastreY] = useState(0);
  const arrastrandoRef = useRef(false);
  const inicioYRef = useRef(0);
  const [modalCta, setModalCta] = useState(null);

  useEffect(() => {
    const enfocadoAntes = document.activeElement;
    const nodo = sheetRef.current;
    const focosables = () => nodo.querySelectorAll('button, a[href], input, [tabindex]:not([tabindex="-1"])');
    focosables()[0]?.focus();

    const manejarTeclado = (evento) => {
      if (evento.key === 'Escape') {
        onCerrar();
        return;
      }
      if (evento.key !== 'Tab') return;
      const lista = Array.from(focosables());
      if (lista.length === 0) return;
      const primero = lista[0];
      const ultimo = lista[lista.length - 1];
      if (evento.shiftKey && document.activeElement === primero) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault();
        primero.focus();
      }
    };

    document.addEventListener('keydown', manejarTeclado);
    return () => {
      document.removeEventListener('keydown', manejarTeclado);
      enfocadoAntes?.focus?.();
    };
  }, [onCerrar]);

  // Swipe-down para cerrar — solo desde la zona del handle, así el resto
  // del sheet (pilares, botones) sigue respondiendo a toques normales.
  const manejarTouchStart = (evento) => {
    arrastrandoRef.current = true;
    inicioYRef.current = evento.touches[0].clientY;
  };
  const manejarTouchMove = (evento) => {
    if (!arrastrandoRef.current) return;
    const delta = evento.touches[0].clientY - inicioYRef.current;
    if (delta > 0) setArrastreY(delta);
  };
  const manejarTouchEnd = () => {
    arrastrandoRef.current = false;
    if (arrastreY > 80) onCerrar();
    else setArrastreY(0);
  };

  const altBanner = `${campaign.party} — ${campaign.candidate}${configuracion.slogan ? `, ${configuracion.slogan}` : ''}`;

  // HU-021: pilares y CTAs con visibilidad configurable.
  // HU-025: cada pilar tiene texto y URL configurables (URL → clickeable).
  const pilaresConfig = [
    { activo: configuracion.campana_pilar_seguridad, texto: configuracion.campana_pilar_1_texto || 'Seguridad', url: configuracion.campana_pilar_1_url || '' },
    { activo: configuracion.campana_pilar_orden, texto: configuracion.campana_pilar_2_texto || 'Orden', url: configuracion.campana_pilar_2_url || '' },
    { activo: configuracion.campana_pilar_experiencia, texto: configuracion.campana_pilar_3_texto || 'Experiencia', url: configuracion.campana_pilar_3_url || '' },
  ];
  const pilaresVisibles = pilaresConfig.filter((p) => p.activo !== false && p.texto);
  const mostrarPilares = configuracion.campana_pilares_activo !== false && pilaresVisibles.length > 0;

  const cta1Url = configuracion.campana_cta_url || campaign.ctaUrl;
  const cta2Url = configuracion.campana_cta2_url || '';
  const cta3Url = configuracion.campana_cta3_url || '';
  const cta4Url = configuracion.campana_cta4_url || '';
  const mostrarCta1 = configuracion.campana_cta_activo !== false;
  const mostrarCta2 = !!configuracion.campana_cta2_activo;
  const mostrarCta3 = !!configuracion.campana_cta3_activo;
  const mostrarCta4 = !!configuracion.campana_cta4_activo;

  const abrirCta = (url, enModal) => {
    if (!url) return;
    if (enModal) setModalCta(url);
    else window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="cs-backdrop" onClick={onCerrar}>
      <div
        ref={sheetRef}
        className="cs-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={`Campaña de ${campaign.party}`}
        onClick={(evento) => evento.stopPropagation()}
        style={arrastreY ? { transform: `translateY(${arrastreY}px)`, transition: 'none' } : undefined}
      >
        <div
          className="cs-sheet-handle-zona"
          onTouchStart={manejarTouchStart}
          onTouchMove={manejarTouchMove}
          onTouchEnd={manejarTouchEnd}
        >
          <span className="cs-sheet-handle"></span>
        </div>

        <img src={configuracion.banner} alt={altBanner} className="cs-sheet-banner cn-kb" />

        <p className="cs-sheet-eyebrow">{campaign.party} · {campaign.candidate}</p>
        <h2 className="cs-sheet-headline">{campaign.headline}</h2>

        {mostrarPilares && (
          <div className="cs-sheet-pilares">
            {pilaresVisibles.map((p, i) => {
              const contenido = (
                <>
                  <span className="cs-sheet-pilar-numero">{i + 1}</span>
                  <span>{p.texto}</span>
                </>
              );
              return p.url ? (
                <a
                  key={p.texto}
                  className="cs-sheet-pilar"
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ animationDelay: `${250 + i * 100}ms`, color: 'inherit', textDecoration: 'none' }}
                >
                  {contenido}
                </a>
              ) : (
                <div key={p.texto} className="cs-sheet-pilar" style={{ animationDelay: `${250 + i * 100}ms` }}>
                  {contenido}
                </div>
              );
            })}
          </div>
        )}

        <div className="cs-sheet-botones">
          <button type="button" className="cs-sheet-boton-secundario" onClick={onCerrar}>
            Seguir reportando
          </button>
          {mostrarCta1 && (
            <button
              type="button"
              className="cs-sheet-boton-cta"
              disabled={!cta1Url}
              onClick={() => abrirCta(cta1Url, configuracion.campana_cta_modal)}
            >
              {configuracion.campana_cta_texto || campaign.ctaLabel}
            </button>
          )}
          {mostrarCta2 && (
            <button
              type="button"
              className="cs-sheet-boton-cta"
              disabled={!cta2Url}
              onClick={() => abrirCta(cta2Url, configuracion.campana_cta2_modal)}
            >
              {configuracion.campana_cta2_texto || 'Ver obras'}
            </button>
          )}
          {mostrarCta3 && (
            <button
              type="button"
              className="cs-sheet-boton-cta"
              disabled={!cta3Url}
              onClick={() => abrirCta(cta3Url, configuracion.campana_cta3_modal)}
            >
              {configuracion.campana_cta3_texto || 'Mis obras'}
            </button>
          )}
          {mostrarCta4 && (
            <button
              type="button"
              className="cs-sheet-boton-cta"
              disabled={!cta4Url}
              onClick={() => abrirCta(cta4Url, configuracion.campana_cta4_modal)}
            >
              {configuracion.campana_cta4_texto || 'Propuestas'}
            </button>
          )}
        </div>
      </div>

      {modalCta && <ModalCta url={modalCta} onCerrar={() => setModalCta(null)} />}
    </div>
  );
};

export default CampaignSheet;
